import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "./context/CartContext";
import { ProfileProvider } from "./context/ProfileContext";
import { AuthProvider } from "./context/AuthContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { TopMarquee } from "./components/Marquee";
import { CartDrawer } from "./components/CartDrawer";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { initSentry, attachAxiosInterceptor } from "./lib/sentry";
import { api } from "./lib/api";
import { Home } from "./pages/Home";              // eager — landing page
import { Login } from "./pages/Login";            // eager — entry point
import { AuthCallback } from "./pages/AuthCallback"; // eager — auth round-trip

// Code-split everything else. Pages chunk by route on demand → smaller first-paint bundle.
const Shop                = lazy(() => import("./pages/Shop").then(m => ({ default: m.Shop })));
const ProductDetail       = lazy(() => import("./pages/ProductDetail").then(m => ({ default: m.ProductDetail })));
const Cart                = lazy(() => import("./pages/Cart").then(m => ({ default: m.Cart })));
const CartRestore         = lazy(() => import("./pages/CartRestore").then(m => ({ default: m.CartRestore })));
const CheckoutSuccess     = lazy(() => import("./pages/CheckoutSuccess").then(m => ({ default: m.CheckoutSuccess })));
const Admin               = lazy(() => import("./pages/Admin").then(m => ({ default: m.Admin })));
const ProfilePage         = lazy(() => import("./pages/Profile").then(m => ({ default: m.ProfilePage })));
const Matches             = lazy(() => import("./pages/Matches").then(m => ({ default: m.Matches })));
const Search              = lazy(() => import("./pages/Search").then(m => ({ default: m.Search })));
const Outfits             = lazy(() => import("./pages/Outfits").then(m => ({ default: m.Outfits })));
const AdminProfiles       = lazy(() => import("./pages/AdminProfiles").then(m => ({ default: m.AdminProfiles })));
const Returns             = lazy(() => import("./pages/Returns").then(m => ({ default: m.Returns })));
const AdminReturns        = lazy(() => import("./pages/AdminReturns").then(m => ({ default: m.AdminReturns })));
const AdminWaitlist       = lazy(() => import("./pages/AdminWaitlist").then(m => ({ default: m.AdminWaitlist })));
const AdminAnalytics      = lazy(() => import("./pages/AdminAnalytics").then(m => ({ default: m.AdminAnalytics })));
const PublicLook          = lazy(() => import("./pages/PublicLook").then(m => ({ default: m.PublicLook })));
const AccountSettings     = lazy(() => import("./pages/AccountSettings").then(m => ({ default: m.AccountSettings })));
const AdminCustomerDetail = lazy(() => import("./pages/AdminCustomerDetail").then(m => ({ default: m.AdminCustomerDetail })));
const Privacy             = lazy(() => import("./pages/Legal").then(m => ({ default: m.Privacy })));
const Terms               = lazy(() => import("./pages/Legal").then(m => ({ default: m.Terms })));
const ReturnsPolicy       = lazy(() => import("./pages/Legal").then(m => ({ default: m.ReturnsPolicy })));
const Contact             = lazy(() => import("./pages/Contact").then(m => ({ default: m.Contact })));
const About               = lazy(() => import("./pages/About").then(m => ({ default: m.About })));
const WishlistPage        = lazy(() => import("./pages/Wishlist").then(m => ({ default: m.WishlistPage })));
const NotFound            = lazy(() => import("./pages/NotFound").then(m => ({ default: m.NotFound })));
import { OnboardingGate } from "./components/OnboardingGate";
import { CookieBanner } from "./components/CookieBanner";
import { InstallAppPrompt } from "./components/InstallAppPrompt";

// Brutalist suspense fallback — single source for all lazy routes.
const RouteFallback = () => (
    <div data-testid="route-loading" className="py-24 text-center font-mono text-xs uppercase tracking-widest text-black/60">
        Loading&hellip;
    </div>
);

// Boot Sentry once at module load. No-op until REACT_APP_SENTRY_DSN is set.
initSentry();
attachAxiosInterceptor(api);

function App() {
    return (
        <div className="App">
            <ErrorBoundary>
                <AuthProvider>
                <ProfileProvider>
                    <CartProvider>
                        <BrowserRouter>
                            <a
                                href="#main-content"
                                data-testid="skip-to-content"
                                className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-black focus:text-[#CCFF00] focus:border-2 focus:border-[#CCFF00] focus:px-3 focus:py-2 focus:font-mono focus:text-xs focus:uppercase focus:tracking-widest"
                            >
                                Skip to main content
                            </a>
                            <TopMarquee />
                            <Navbar />
                            <CartDrawer />
                            <main id="main-content" tabIndex="-1" className="min-h-[60vh]">
                                <Suspense fallback={<RouteFallback />}>
                                    <Routes>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/shop" element={<Shop />} />
                                        <Route path="/product/:id" element={<ProductDetail />} />
                                        <Route path="/cart" element={<Cart />} />
                                        <Route path="/cart/restore" element={<CartRestore />} />
                                        <Route path="/checkout/success" element={<CheckoutSuccess />} />
                                        <Route path="/admin" element={<Admin />} />
                                        <Route path="/admin/profiles" element={<AdminProfiles />} />
                                        <Route path="/admin/customers/:user_id" element={<AdminCustomerDetail />} />
                                        <Route path="/admin/returns" element={<AdminReturns />} />
                                        <Route path="/admin/waitlist" element={<AdminWaitlist />} />
                                        <Route path="/admin/analytics" element={<AdminAnalytics />} />
                                        <Route path="/looks/:shareId" element={<PublicLook />} />
                                        <Route path="/returns" element={<Returns />} />
                                        <Route path="/account" element={<AccountSettings />} />
                                        <Route path="/privacy" element={<Privacy />} />
                                        <Route path="/terms" element={<Terms />} />
                                        <Route path="/returns-policy" element={<ReturnsPolicy />} />
                                        <Route path="/contact" element={<Contact />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/profile" element={<ProfilePage />} />
                                        <Route path="/matches" element={<Matches />} />
                                        <Route path="/search" element={<Search />} />
                                        <Route path="/outfits" element={<Outfits />} />
                                        <Route path="/login" element={<Login />} />
                                        <Route path="/auth/callback" element={<AuthCallback />} />
                                        <Route path="/wishlist" element={<WishlistPage />} />
                                        <Route path="*" element={<NotFound />} />
                                    </Routes>
                                </Suspense>
                            </main>
                            <Footer />
                            <OnboardingGate />
                            <CookieBanner />
                            <InstallAppPrompt />
                            <Toaster position="top-right" />
                        </BrowserRouter>
                    </CartProvider>
                </ProfileProvider>
            </AuthProvider>
            </ErrorBoundary>
        </div>
    );
}

export default App;
